import React from 'react';
import { motion } from 'framer-motion';

function Careers() {
  const roles = [
    {
      title: 'P2P Partners – Crypto & Fiat (Primary Focus)',
      location: 'Global / Remote',
      summary:
        'Entrepreneurial partners who can originate, onboard and support peer‑to‑peer investors and liquidity providers across regions — with a strong focus on Crypto P2P and compliant on/off‑ramping.',
      bullets: [
        'Grow regional P2P investor communities and partnerships (crypto & fiat)',
        'Educate prospects on our platform, risk, and compliance',
        'Coordinate KYC/AML onboarding with operations (exchange/wallet KYC where required)',
        'Support on/off‑ramp flows, wallet literacy and dispute resolution',
        'Track pipeline, report performance, and optimize conversion',
      ],
      badge: 'High Priority',
    },
    {
      title: 'Investment Analyst – Multi‑Asset',
      location: 'London / Hybrid',
      summary:
        'Support portfolio managers with research, modeling, and implementation across public markets and alternatives.',
      bullets: [
        'Macro/sector research and data analysis',
        'Screen managers, ETFs and funds; maintain due‑diligence files',
        'Build dashboards for risk, factor, and performance attribution',
        'Contribute to investment committee notes',
      ],
    },
    {
      title: 'Client Advisor – Wealth Management',
      location: 'Manchester / Hybrid',
      summary:
        'Advise HNW clients on planning and investments; coordinate portfolios, tax and estate stakeholders.',
      bullets: [
        'Discovery, proposals and onboarding of HNW clients',
        'Quarterly reviews and event‑driven communications',
        'Coordinate with research, trading, tax and legal partners',
        'Maintain CRM hygiene and growth objectives',
      ],
    },
    {
      title: 'Compliance & Risk Associate',
      location: 'Remote / Hybrid',
      summary:
        'Strengthen supervisory controls across KYC/AML, suitability, and product governance for traditional and digital assets.',
      bullets: [
        'KYC/AML monitoring and case management',
        'Surveillance of marketing, trading, and client communications',
        'Policy documentation; regulator/partner liaison',
        'Incident reporting and remediation tracking',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative h-[50vh] overflow-hidden">
        <img
          src="/images/business-people-hiring-job-offer-with-group-documents-contract-employment-b2b-deal-interview-staff-employees-teamwork-merger-project-human-resources-with-paperwork_590464-342156.jpg_tupxel.jpg"
          alt="Careers"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black/80" />
        <div className="relative z-10 flex h-full items-center justify-center text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <h1 className="text-white text-5xl font-light mb-4">
              Join <span className="font-semibold text-[#fbbf24]">Blockfirst Capital</span>
            </h1>
            <p className="text-white/90 text-xl max-w-3xl mx-auto">
              Build the future of wealth with a high‑ownership, client‑first team. We’re hiring across research, advisory, risk — with a strong emphasis on P2P partners.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Open Roles */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div data-aos="fade-up" className="text-center mb-14">
            <h2 className="text-4xl font-semibold text-gray-900">Open Roles</h2>
            <p className="text-lg text-gray-600 mt-2">
              We hire for character, craft and client obsession. Remote‑friendly — with hubs in London and Manchester.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {roles.map((role, i) => (
              <div
                key={role.title}
                data-aos="fade-up"
                data-aos-delay={`${150 + i * 120}`}
                className="p-8 bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-2xl font-semibold text-gray-900">{role.title}</h3>
                    <div className="text-sm text-[#0056d7] font-medium mt-1">{role.location}</div>
                  </div>
                  {role.badge && (
                    <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 text-xs font-semibold px-3 py-1">
                      {role.badge}
                    </span>
                  )}
                </div>
                <p className="text-gray-600 mt-4">{role.summary}</p>
                <ul className="mt-4 space-y-2 text-gray-700 text-sm">
                  {role.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <span className="mt-1 w-2 h-2 rounded-full bg-[#0056d7]" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  <motion.a
                    href="/contact"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] hover:from-[#f59e0b] hover:to-[#d97706] text-black font-medium transition-all duration-500 shadow-lg hover:shadow-amber-500/25"
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.96 }}
                  >
                    Apply / Partner
                  </motion.a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* P2P Partners spotlight */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div data-aos="fade-right">
            <h3 className="text-3xl font-semibold text-gray-900 mb-3">P2P Partners Program (Crypto & Fiat)</h3>
            <p className="text-gray-700 text-lg mb-6">
              We’re building a network of high‑integrity P2P partners (crypto and fiat) who connect investors to our
              platform and nurture long‑term relationships. You’ll operate like an owner — with transparent economics
              and full compliance support.
            </p>
            <ul className="space-y-3">
              {[
                'Attractive revenue‑share with transparent, on‑time payouts',
                'Dedicated enablement: content, events, and digital tools',
              'Compliance first: clear KYC/AML and suitability workflows (including exchange/wallet checks)',
                'Territory flexibility — collaborate across global markets',
              'Crypto expertise welcome: OTC flows, stablecoin rails and local on/off‑ramps',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1 w-2 h-2 rounded-full bg-[#0056d7]" />
                  <span className="text-gray-800">{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <motion.a
                href="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] hover:from-[#f59e0b] hover:to-[#d97706] text-black font-semibold transition-all duration-500 shadow-xl hover:shadow-amber-500/30"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.96 }}
              >
                Become a P2P Partner
              </motion.a>
            </div>
          </div>
          <div data-aos="fade-left" className="">
            <img
            src="/images/business-people-meeting-with-opinion-laptop-ideas-collaboration-project-feedback-with-coaching-teamwork-online-review-research-man-women-with-target-management-modern-office_590464-200806.jpg_vbhl80.jpg"
              alt="P2P Partners"
              className="w-full h-80 object-cover rounded-2xl shadow-lg"
            />
          </div>
        </div>
      </section>
    </div>
  );
}

export default Careers;




